// Shared animated confirmation modal. Supports an optional rich `details`
// block (e.g. an impact list), a type-to-confirm word for irreversible
// actions, and a loading state while the confirmed action runs.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Trash2, X, Info, CheckCircle, Loader2 } from 'lucide-react';

interface CuteConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  itemName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  /** Extra content rendered under the message (impact lists, hints…). */
  details?: React.ReactNode;
  /** When set, the user must type this word before the confirm button enables. */
  confirmWord?: string;
  /** Disables both buttons and shows a spinner on confirm. */
  isLoading?: boolean;
  /** Widen the card for richer content. */
  size?: 'sm' | 'md';
  onConfirm: () => void;
  onCancel: () => void;
}

export const CuteConfirmModal: React.FC<CuteConfirmModalProps> = ({
  isOpen,
  title,
  message,
  itemName,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'danger',
  details,
  confirmWord,
  isLoading = false,
  size = 'sm',
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);

  if (!isOpen) return null;

  const confirmDisabled = isLoading || (!!confirmWord && typed.trim() !== confirmWord);

  const variantStyles = {
    danger: {
      iconBg: 'bg-gradient-to-br from-red-100 to-red-200',
      iconColor: 'text-red-500',
      buttonBg: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-red-200',
      ringColor: 'ring-red-100',
      Icon: Trash2,
    },
    warning: {
      iconBg: 'bg-gradient-to-br from-amber-100 to-amber-200',
      iconColor: 'text-amber-500',
      buttonBg: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-200',
      ringColor: 'ring-amber-100',
      Icon: AlertTriangle,
    },
    info: {
      iconBg: 'bg-gradient-to-br from-blue-100 to-blue-200',
      iconColor: 'text-blue-500',
      buttonBg: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-blue-200',
      ringColor: 'ring-blue-100',
      Icon: Info,
    },
    success: {
      iconBg: 'bg-gradient-to-br from-green-100 to-green-200',
      iconColor: 'text-green-500',
      buttonBg: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-green-200',
      ringColor: 'ring-green-100',
      Icon: CheckCircle,
    },
  };

  const { iconBg, iconColor, buttonBg, ringColor, Icon } = variantStyles[variant];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div
        className={`bg-white rounded-3xl shadow-2xl w-full ${size === 'md' ? 'max-w-lg' : 'max-w-sm'} overflow-hidden transform transition-all relative`}
        style={{
          animation: 'modalPop 0.3s ease-out'
        }}
      >
        <style>{`
          @keyframes modalPop {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes iconBounce {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>

        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div className="px-6 pt-8 pb-6 text-center">
          {/* Animated Icon */}
          <div className="flex justify-center mb-5">
            <div
              className={`p-5 rounded-full ${iconBg} ring-8 ${ringColor}`}
              style={{ animation: 'iconBounce 0.5s ease-out' }}
            >
              <Icon size={36} className={iconColor} strokeWidth={2} />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {title}
          </h3>

          {/* Item name highlight */}
          {itemName && (
            <div className="mb-3 px-4 py-2 bg-gray-100 rounded-xl inline-block">
              <span className="text-sm font-medium text-gray-700">
                "{itemName}"
              </span>
            </div>
          )}

          {/* Message */}
          <p className="text-gray-500 text-sm leading-relaxed">
            {message}
          </p>

          {details && <div className="mt-4 text-left">{details}</div>}

          {confirmWord && (
            <div className="mt-4 text-left">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Tapez <span className="font-mono font-bold text-gray-900">{confirmWord}</span> pour confirmer
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                placeholder={confirmWord}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            type="button"
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-gray-600 bg-gray-100 rounded-xl font-medium hover:bg-gray-200 transition-all active:scale-95 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => {
              if (!confirmDisabled) onConfirm();
            }}
            type="button"
            disabled={confirmDisabled}
            className={`flex-1 px-4 py-3 text-white rounded-xl font-medium transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${buttonBg}`}
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CuteConfirmModal;
